import { useState } from "react";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import api from "@/lib/api";

const H = { fontFamily: "Plus Jakarta Sans, sans-serif" };
const INIT = { name: "", company: "", email: "", phone: "", interest: "", message: "", privacy: false, marketing_consent: false, website: "" };
export const INTERESTS = [["whatsapp", "WhatsApp Business"], ["sms", "SMS"], ["crm_multicanale", "CRM multicanale"], ["integrazioni", "Integrazioni"]];
const cls = "w-full rounded-xl border !border-slate-700 !bg-[#0A0E17] px-3.5 py-2.5 text-sm !text-white placeholder:text-slate-500 focus:outline-none focus:border-[#10B981] transition-colors";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^\+?[0-9 .\-()]{6,20}$/;
const validate = (f) => {
  if (f.name.trim().length < 2) return "Inserisci nome e cognome.";
  if (f.company.trim().length < 2) return "Inserisci il nome dell'azienda.";
  if (!EMAIL_RE.test(f.email.trim())) return "Inserisci un indirizzo email aziendale valido.";
  if (f.phone.trim() && !PHONE_RE.test(f.phone.trim())) return "Inserisci un numero di telefono valido (es. +39 333 1234567).";
  if (!f.interest) return "Indica l'interesse principale.";
  if (!f.privacy) return "Per inviare la richiesta devi confermare la presa visione dell'informativa privacy.";
  return "";
};

const Field = ({ label, children, required }) => (
  <label className="block text-sm">
    <span className="text-slate-300">{label}{required && <span className="text-[#10B981]"> *</span>}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

export const DemoRequestForm = ({ defaultInterest = "" }) => {
  const [f, setF] = useState({ ...INIT, interest: defaultInterest });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validate(f);
    if (v) return setError(v);
    setLoading(true);
    try {
      await api.post("/public/demo-request", f);
      setDone(true);
    } catch (err) {
      const s = err.response?.status;
      setError(s === 429 ? "Hai inviato troppe richieste: riprova più tardi." : s === 422 ? "Controlla i dati inseriti e riprova." : "Invio non riuscito, riprova tra qualche istante.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-[#10B981]/40 bg-[#10B981]/10 p-8 text-center" data-testid="demo-form-success">
        <CheckCircle2 className="mx-auto h-10 w-10 text-[#10B981]" />
        <p className="mt-3 text-lg font-semibold text-white" style={H}>Richiesta inviata, grazie!</p>
        <p className="mt-1 text-sm text-slate-300">Studio Nouvelle ti ricontatterà entro un giorno lavorativo ai recapiti indicati.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="demo-form" noValidate>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nome e cognome" required><input required className={cls} value={f.name} onChange={set("name")} autoComplete="name" data-testid="demo-name" /></Field>
        <Field label="Azienda" required><input required className={cls} value={f.company} onChange={set("company")} autoComplete="organization" data-testid="demo-company" /></Field>
        <Field label="Email aziendale" required><input required type="email" className={cls} value={f.email} onChange={set("email")} autoComplete="email" data-testid="demo-email" /></Field>
        <Field label="Telefono (facoltativo)"><input type="tel" className={cls} value={f.phone} onChange={set("phone")} autoComplete="tel" placeholder="+39 …" data-testid="demo-phone" /></Field>
      </div>
      <Field label="Interesse principale" required>
        <div className="flex flex-wrap gap-2" role="radiogroup" data-testid="demo-interest">
          {INTERESTS.map(([v, l]) => (
            <button type="button" key={v} onClick={() => setF((s) => ({ ...s, interest: v }))} aria-pressed={f.interest === v} data-testid={`demo-interest-${v}`}
              className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${f.interest === v ? "border-[#10B981] bg-[#10B981]/15 text-white" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>{l}</button>
          ))}
        </div>
      </Field>
      <Field label="Messaggio (facoltativo)"><textarea rows={3} className={cls} value={f.message} onChange={set("message")} placeholder="Raccontaci la tua attività e cosa vorresti ottenere" data-testid="demo-message" /></Field>
      <input type="text" name="website" value={f.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <label className="flex items-start gap-3 text-sm text-slate-300">
        <input type="checkbox" checked={f.privacy} onChange={set("privacy")} className="mt-0.5 h-4 w-4 accent-[#10B981]" data-testid="demo-privacy" />
        <span>Ho preso visione dell'<a href="/privacy" target="_blank" rel="noreferrer" className="text-[#10B981] hover:underline">informativa privacy</a>: i dati saranno usati solo per ricontattarmi in merito alla richiesta. <span className="text-[#10B981]">*</span></span>
      </label>
      <label className="flex items-start gap-3 text-sm text-slate-400">
        <input type="checkbox" checked={f.marketing_consent} onChange={set("marketing_consent")} className="mt-0.5 h-4 w-4 accent-[#10B981]" data-testid="demo-marketing-consent" />
        <span>Facoltativo: acconsento a ricevere da Studio Nouvelle aggiornamenti commerciali su NOUVELLE WHATSAPP via email. Revocabile in qualsiasi momento.</span>
      </label>
      {error && <p className="text-sm text-red-400" data-testid="demo-form-error">{error}</p>}
      <button type="submit" disabled={loading} className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] disabled:opacity-60 text-slate-950 font-semibold px-6 py-3 transition-colors" data-testid="demo-submit">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia la richiesta
      </button>
    </form>
  );
};
